import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LogOut, Home, Save } from 'lucide-react';

const AdminAbout = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchContent();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/about-content', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('İçerik yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Çıkış yapıldı');
  };

  const handleChange = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/about-content', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(content),
      });

      if (response.ok) {
        toast.success('İçerik kaydedildi');
      } else {
        toast.error('Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-[#1e4a7a]">MİGA Yapı - Admin Panel</h1>
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Siteyi Görüntüle
                </Button>
              </Link>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link to="/admin/dashboard" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Ana Sayfa İçeriği
            </Link>
            <Link to="/admin/about" className="text-[#1e4a7a] font-medium border-b-2 border-[#1e4a7a] pb-4">
              Kurumsal
            </Link>
            <Link to="/admin/services" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Hizmetlerimiz
            </Link>
            <Link to="/admin/contact" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
            <Link to="/admin/faq" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              SSS
            </Link>
              İletişim
            <Link to="/admin/faq" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              SSS
            </Link>
            </Link>
            <Link to="/admin/projects" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Proje Yönetimi
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Kurumsal Sayfa İçeriği</h2>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Header Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Başlık Bölümü</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Sayfa Başlığı</Label>
                  <Input
                    value={content?.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Alt Başlık</Label>
                  <Textarea
                    value={content?.subtitle || ''}
                    onChange={(e) => handleChange('subtitle', e.target.value)}
                    rows={2}
                    required
                  />
                </div>
              </div>
            </div>

            {/* About Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Hakkımızda Bölümü</h3>
              <div className="space-y-4">
                <div>
                  <Label>Hakkımızda Metin 1</Label>
                  <Textarea
                    value={content?.about_text_1 || ''}
                    onChange={(e) => handleChange('about_text_1', e.target.value)}
                    rows={3}
                    required
                  />
                </div>
                <div>
                  <Label>Hakkımızda Metin 2</Label>
                  <Textarea
                    value={content?.about_text_2 || ''}
                    onChange={(e) => handleChange('about_text_2', e.target.value)}
                    rows={3}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Mission & Vision */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Misyon & Vizyon</h3>
              <div className="space-y-4">
                <div>
                  <Label>Misyon</Label>
                  <Textarea
                    value={content?.mission_text || ''}
                    onChange={(e) => handleChange('mission_text', e.target.value)}
                    rows={3}
                    required
                  />
                </div>
                <div>
                  <Label>Vizyon</Label>
                  <Textarea
                    value={content?.vision_text || ''}
                    onChange={(e) => handleChange('vision_text', e.target.value)}
                    rows={3}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Values */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Değerlerimiz</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Değer 1 Başlık</Label>
                  <Input
                    value={content?.value1_title || ''}
                    onChange={(e) => handleChange('value1_title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Değer 1 Açıklama</Label>
                  <Input
                    value={content?.value1_desc || ''}
                    onChange={(e) => handleChange('value1_desc', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Değer 2 Başlık</Label>
                  <Input
                    value={content?.value2_title || ''}
                    onChange={(e) => handleChange('value2_title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Değer 2 Açıklama</Label>
                  <Input
                    value={content?.value2_desc || ''}
                    onChange={(e) => handleChange('value2_desc', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Değer 3 Başlık</Label>
                  <Input
                    value={content?.value3_title || ''}
                    onChange={(e) => handleChange('value3_title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Değer 3 Açıklama</Label>
                  <Input
                    value={content?.value3_desc || ''}
                    onChange={(e) => handleChange('value3_desc', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Değer 4 Başlık</Label>
                  <Input
                    value={content?.value4_title || ''}
                    onChange={(e) => handleChange('value4_title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Değer 4 Açıklama</Label>
                  <Input
                    value={content?.value4_desc || ''}
                    onChange={(e) => handleChange('value4_desc', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Stats */}
            <div>
              <h3 className="text-lg font-semibold mb-4">İstatistikler</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Deneyim (örn: 30+)</Label>
                  <Input
                    value={content?.stat_experience || ''}
                    onChange={(e) => handleChange('stat_experience', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Proje Sayısı (örn: 25+)</Label>
                  <Input
                    value={content?.stat_projects || ''}
                    onChange={(e) => handleChange('stat_projects', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Müşteri Sayısı (örn: 500+)</Label>
                  <Input
                    value={content?.stat_clients || ''}
                    onChange={(e) => handleChange('stat_clients', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-6 border-t">
              <Button type="submit" disabled={saving} className="bg-[#dc2626] hover:bg-[#b91c1c]">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminAbout;
